import React, { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { logout } from "../../store/slices/authSlice";
import { io, Socket } from "socket.io-client";
import cookieService from "../../services/cookieService";
import { motion } from "framer-motion";
import TenantNavigationSidebar from "../navigation/TenantNavigationSidebar";
import MobileBottomNav from "../navigation/MobileBottomNav";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  addNotification,
  setNotifications,
} from "../../store/slices/notificationsSlice";
import { getUserNotifications } from "../../services/tenant/notifications";
import { BRAND_NAME } from "../../config/brand";

const TenantLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem("tenantSidebarCollapsed");
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });
  // Track if screen is smaller than Tailwind's lg breakpoint (1024px)
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(() => {
    try {
      return typeof window !== 'undefined'
        ? window.matchMedia('(max-width: 1023px)').matches
        : false;
    } catch {
      return false;
    }
  });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const unreadCount = useSelector(
    (state: RootState) => state.notifications.unreadCount
  );
  const socketRef = useRef<Socket | null>(null);

  // Keep isSmallScreen in sync with window size
  useEffect(() => {
    try {
      const mql = window.matchMedia('(max-width: 1023px)');
      const onChange = (e: MediaQueryListEvent) => setIsSmallScreen(e.matches);
      setIsSmallScreen(mql.matches);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    } catch {
      // matchMedia not available
    }
  }, []);

  useEffect(() => {
    // Initialize socket connection
    if (user) {
      const token = cookieService.get("token");
      socketRef.current = io(
        process.env.REACT_APP_SOCKET_URL || "http://localhost:5002",
        {
          auth: { token },
          transports: ["websocket", "polling"],
          withCredentials: true,
        }
      );

      socketRef.current.on("connect", () => {
        console.log("Socket connected");
      });

      socketRef.current.on("connect_error", (error) => {
        console.error("Socket connection error:", error);
      });

      socketRef.current.on("disconnect", () => {
        console.log("Socket disconnected");
      });

      // Real-time notifications for tenant
      socketRef.current.on("notification:new", (payload: any) => {
        try {
          dispatch(addNotification(payload));
        } catch {}
      });
      socketRef.current.on("notification:count", async (_count: number) => {
        // Optionally refresh list to sync count and items
        try {
          const res = await getUserNotifications({
            status: "all",
            page: 1,
            limit: 20,
          });
          if (res.success) {
            dispatch(setNotifications(res.data));
          }
        } catch {}
      });
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [user]);

  // Initial notifications fetch on mount (tenant only)
  useEffect(() => {
    const fetchInitial = async () => {
      if (!user) return;
      try {
        const res = await getUserNotifications({
          status: "all",
          page: 1,
          limit: 20,
        });
        if (res.success) {
          dispatch(setNotifications(res.data));
        }
      } catch {}
    };
    void fetchInitial();
  }, [user, dispatch]);

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
    } catch (error) {
      // Handle logout error if needed
    }
    navigate("/");
  };

  // Force expanded sidebar on small screens (match Dashboard behavior)
  const effectiveCollapsed = isSmallScreen ? false : sidebarCollapsed;
  const targetWidth = effectiveCollapsed ? 80 : 220;

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar - hidden on mobile, visible on lg+ */}
      <motion.aside
        animate={{ width: targetWidth }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        className="hidden lg:flex lg:flex-col fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 lg:static lg:h-full overflow-x-hidden"
        style={{ width: targetWidth }}
      >
        <div className={`min-h-[64px] flex items-center justify-between px-2 bg-white border-b border-gray-200 gap-[5px]`}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center">
              <img src="/assets/rentBhaiLogo.svg" alt={`${BRAND_NAME} Logo`} className="w-10 h-10" />
            </div>
            {!effectiveCollapsed && (
              <h1 className="text-purple-600 text-lg font-bold tracking-tight whitespace-nowrap">
                {BRAND_NAME}
              </h1>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setSidebarCollapsed((prev) => {
                const next = !prev;
                try {
                  localStorage.setItem("tenantSidebarCollapsed", JSON.stringify(next));
                } catch {}
                return next;
              });
            }}
            className={`hidden lg:inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-600 hover:text-purple-600 hover:bg-purple-50 transition`}
            aria-label={effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-pressed={effectiveCollapsed}
            title={effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {effectiveCollapsed ? (
              <ChevronRightIcon className="w-6 h-6" />
            ) : (
              <ChevronLeftIcon className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <TenantNavigationSidebar
          onItemClick={() => setSidebarOpen(false)}
          collapsed={effectiveCollapsed}
        />

        {/* Bottom user section and sign out */}
        <div className="flex-shrink-0 p-3 border-t border-gray-200 bg-white mt-auto">
          {effectiveCollapsed ? (
            <div className="flex items-center justify-center">
              <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                {user?.firstName?.charAt(0)}
                {user?.lastName?.charAt(0)}
              </div>
            </div>
          ) : (
            <div className="mb-3">
              <button
                onClick={() => navigate('/tenant/profile')}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-all group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 group-hover:scale-105 transition-transform shadow-md">
                  {user?.firstName?.charAt(0)}
                  {user?.lastName?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-purple-600 transition-colors">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">Tenant</p>
                </div>
                <svg className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          <button
            onClick={handleLogout}
            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-all flex items-center gap-2 font-medium group"
            aria-label="Sign out"
            title="Sign out"
          >
            <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign out
          </button>
        </div>
      </motion.aside>

      {/* Mobile sidebar overlay - hidden on mobile since we use bottom nav */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Page content - add bottom padding on mobile for bottom nav */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto">
          <div className="pb-20 lg:pb-0">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation - visible only on mobile */}
      <MobileBottomNav />
    </div>
  );
};

export default TenantLayout;
